'use client';

import { useState } from 'react';
import ServiceCard from './ServiceCard';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: string;
  category: 'onward-tickets' | 'insurance' | 'transfers' | 'visa' | 'quotes';
  title: string;
  description: string;
  price: number;
  priceLabel: string;
  image: string;
  alt: string;
  features: string[];
  popular?: boolean;
  verified: boolean;
  rating: number;
  reviewCount: number;
  provider: string;
  ctaText: string;
  ctaLink: string;
}

export default function MarketplaceInteractive() {
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const categories = [
  { id: 'all', label: 'All Services', icon: 'Squares2X2Icon' },
  { id: 'onward-tickets', label: 'Onward Tickets', icon: 'DocumentCheckIcon' },
  { id: 'insurance', label: 'Travel Insurance', icon: 'ShieldCheckIcon' },
  { id: 'transfers', label: 'Airport Transfers', icon: 'TruckIcon' },
  { id: 'visa', label: 'Visa Services', icon: 'DocumentTextIcon' },
  { id: 'quotes', label: 'Travel Quotes', icon: 'CurrencyPoundIcon' }];


  const services: Service[] = [
  {
    id: 'onward-1',
    category: 'onward-tickets',
    title: 'Embassy-Verified Onward Ticket',
    description: 'Valid PNR with instant delivery for visa applications and airline check-in',
    price: 14.99,
    priceLabel: 'from £14.99',
    image: "https://images.unsplash.com/photo-1705165371908-e9adaa7f25f1",
    alt: 'Airplane wing view during flight over clouds at sunset',
    features: ['Active PNR code', 'Embassy verified', 'Instant delivery', '7-day validity'],
    popular: true,
    verified: true,
    rating: 4.9,
    reviewCount: 2847,
    provider: 'Dial A Flight',
    ctaText: 'Book Now',
    ctaLink: '/onward-ticket'
  },
  {
    id: 'insurance-1',
    category: 'insurance',
    title: 'Comprehensive Travel Insurance',
    description: 'Medical coverage, trip cancellation, baggage protection, and emergency assistance',
    price: 29.99,
    priceLabel: 'from £29.99',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10b8cb9f7-1772297240264.png",
    alt: 'Travel insurance documents with passport and boarding pass on desk',
    features: ['Medical coverage up to £1M', 'Trip cancellation', 'Lost baggage', '24/7 assistance'],
    verified: true,
    rating: 4.8,
    reviewCount: 1523,
    provider: 'TravelGuard Pro',
    ctaText: 'Get Quote',
    ctaLink: '/marketplace?service=insurance'
  },
  {
    id: 'insurance-2',
    category: 'insurance',
    title: 'Budget Travel Protection',
    description: 'Essential coverage for medical emergencies and trip interruptions',
    price: 15.99,
    priceLabel: 'from £15.99',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d59b32c5-1772126506975.png",
    alt: 'Medical kit and travel documents arranged on wooden table',
    features: ['Medical up to £250K', 'Emergency evacuation', 'Basic baggage cover', 'Online claims'],
    verified: true,
    rating: 4.6,
    reviewCount: 892,
    provider: 'SafeTrip Basic',
    ctaText: 'Get Quote',
    ctaLink: '/marketplace?service=insurance'
  },
  {
    id: 'transfer-1',
    category: 'transfers',
    title: 'Private Airport Transfer',
    description: 'Door-to-door luxury transfer with professional driver and flight tracking',
    price: 45.00,
    priceLabel: 'from £45.00',
    image: "https://images.unsplash.com/photo-1655827763464-0191f35ca779",
    alt: 'Luxury black sedan parked at airport terminal entrance',
    features: ['Professional driver', 'Flight tracking', 'Meet & greet', 'Free cancellation'],
    verified: true,
    rating: 4.9,
    reviewCount: 3421,
    provider: 'AirportLink Premium',
    ctaText: 'Book Transfer',
    ctaLink: '/marketplace?service=transfer'
  },
  {
    id: 'transfer-2',
    category: 'transfers',
    title: 'Shared Shuttle Service',
    description: 'Affordable shared transfer with multiple pickup points and flexible timing',
    price: 18.00,
    priceLabel: 'from £18.00',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ba375795-1772126506364.png",
    alt: 'Airport shuttle bus at terminal with passengers boarding',
    features: ['Shared ride', 'Multiple stops', 'Budget-friendly', 'Reliable schedule'],
    verified: true,
    rating: 4.5,
    reviewCount: 1876,
    provider: 'EcoShuttle',
    ctaText: 'Book Transfer',
    ctaLink: '/marketplace?service=transfer'
  },
  {
    id: 'visa-1',
    category: 'visa',
    title: 'Express Visa Processing',
    description: 'Fast-track visa application with document verification and embassy liaison',
    price: 89.99,
    priceLabel: 'from £89.99',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_123c7a06d-1767270855073.png",
    alt: 'Passport with visa stamps and application forms on desk',
    features: ['Document review', 'Embassy liaison', 'Express processing', 'Status tracking'],
    popular: true,
    verified: true,
    rating: 4.8,
    reviewCount: 2156,
    provider: 'VisaExpress Global',
    ctaText: 'Apply Now',
    ctaLink: '/visa-requirements-matrix'
  },
  {
    id: 'visa-2',
    category: 'visa',
    title: 'Visa Consultation Service',
    description: 'Expert guidance on visa requirements, document preparation, and application strategy',
    price: 49.99,
    priceLabel: 'from £49.99',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b7807aa3-1772126511090.png",
    alt: 'Visa consultant reviewing documents with client in office',
    features: ['1-on-1 consultation', 'Document checklist', 'Application review', 'Email support'],
    verified: true,
    rating: 4.7,
    reviewCount: 987,
    provider: 'VisaAdvisors',
    ctaText: 'Book Consultation',
    ctaLink: '/visa-requirements-matrix'
  },
  {
    id: 'quote-1',
    category: 'quotes',
    title: 'Multi-Destination Travel Quote',
    description: 'Comprehensive quote for flights, hotels, and activities across multiple destinations',
    price: 0,
    priceLabel: 'Free Quote',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10637fb86-1772297239440.png",
    alt: 'World map with travel pins and itinerary planning documents',
    features: ['Custom itinerary', 'Best price guarantee', 'Flexible dates', 'Expert planning'],
    verified: true,
    rating: 4.9,
    reviewCount: 1654,
    provider: 'Dial A Flight',
    ctaText: 'Get Quote',
    ctaLink: '/flight-search'
  },
  {
    id: 'quote-2',
    category: 'quotes',
    title: 'Group Travel Package Quote',
    description: 'Special rates for group bookings with customized packages and group discounts',
    price: 0,
    priceLabel: 'Free Quote',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d8dad8f2-1772126506698.png",
    alt: 'Group of travelers with backpacks at airport terminal',
    features: ['Group discounts', 'Custom packages', 'Dedicated coordinator', 'Flexible payment'],
    verified: true,
    rating: 4.8,
    reviewCount: 743,
    provider: 'Dial A Flight',
    ctaText: 'Get Quote',
    ctaLink: '/flight-search'
  }];


  const filteredServices = activeCategory === 'all' ?
  services :
  services.filter((service) => service.category === activeCategory);

  return (
    <div className="space-y-8">
      {/* Category Tabs */}
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <div className="flex flex-wrap gap-3 justify-center">
          {categories.map((category) =>
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-all duration-200 ${
            activeCategory === category.id ?
            'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg scale-105' :
            'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'}`
            }>

              <Icon name={category.icon} size={20} />
              <span>{category.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
            activeCategory === category.id ? 'bg-white/20' : 'bg-gray-200'}`
            }>
                {category.id === 'all' ? services.length : services.filter((s) => s.category === category.id).length}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredServices.map((service) =>
        <ServiceCard key={service.id} service={service} />
        )}
      </div>

      {/* Empty State */}
      {filteredServices.length === 0 &&
      <div className="text-center py-16">
          <Icon name="MagnifyingGlassIcon" size={64} className="text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No services found</h3>
          <p className="text-gray-600">Try selecting a different category</p>
        </div>
      }
    </div>);

}